import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import ToolCard from '../components/ui/ToolCard';
import CategoryFilters from '../components/ui/CategoryFilters';
import { useTheme } from '../contexts/ThemeContext';
import categories from '../data/categories';
import { getToolsByCategory } from '../data/tools';

/**
 * Компонент CategoryPage - страница категории инструментов
 */
const CategoryPage = () => {
  const { categoryId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(categoryId || 'popular');
  const [tools, setTools] = useState([]);
  const [category, setCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Загрузка данных категории и инструментов
  useEffect(() => {
    // Находим категорию по ID
    const foundCategory = categories.find(c => c.id === categoryId || c.slug === categoryId);
    
    if (foundCategory) {
      setCategory(foundCategory);
      setActiveCategory(foundCategory.id);
      
      // Загружаем инструменты для выбранной категории
      const categoryTools = getToolsByCategory(foundCategory.id);
      setTools(categoryTools);
      
      // Устанавливаем мета-информацию для SEO
      document.title = foundCategory.metaTitle || `${foundCategory.name} | Gensio`;
      
      // Находим мета-описание и устанавливаем его, если есть
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', foundCategory.metaDescription || `Инструменты категории ${foundCategory.name} - Gensio`);
      }
    } else {
      // Если категория не найдена, перенаправляем на главную
      navigate('/');
    }
    
    setIsLoading(false);
  }, [categoryId, navigate]);

  // Обработчик изменения категории
  const handleCategoryChange = (newCategoryId) => {
    // Находим категорию по ID
    const newCategory = categories.find(c => c.id === newCategoryId);
    
    if (newCategory) {
      // Перенаправляем на новую категорию
      navigate(`/category/${newCategory.slug}`);
    }
  };

  // Если данные загружаются, показываем заглушку
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold">Загрузка...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
      {/* Фоновый узор */}
      <div className="fixed inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)] [background-size:20px_20px] opacity-25 pointer-events-none"></div>
      
      {/* Навигационная панель */}
      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      
      {/* Основное содержимое */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Хлебные крошки */}
        <Breadcrumbs />
        
        {/* Заголовок категории */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {category?.name}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400 max-w-3xl">
            {category?.description}
          </p>
        </div>
        
        {/* Фильтры категорий */}
        <CategoryFilters 
          categories={categories} 
          activeCategory={activeCategory} 
          setActiveCategory={handleCategoryChange}
        />
        
        {/* Список инструментов */}
        {tools.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {tools.map((tool) => (
              <ToolCard 
                key={tool.id} 
                tool={tool}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center border border-gray-200 dark:border-gray-700 mb-16">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              В этой категории пока нет инструментов
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Попробуйте выбрать другую категорию или вернитесь позже
            </p>
          </div>
        )}
      </main>
      
      {/* Футер */}
      <Footer />
    </div>
  );
};

export default CategoryPage;